import { useState, useCallback, useEffect } from 'react';

/**
 * Custom hook for search bar functionality
 * @param {Object} options - Configuration options
 * @param {string} options.initialValue - Initial search value
 * @param {Function} options.onSearchChange - Callback when search changes
 * @param {number} options.debounceMs - Debounce delay in milliseconds
 * @param {boolean} options.clearOnMount - Whether to clear search on component mount
 * @returns {Object} Search bar state and handlers
 */
export const useSearchBar = ({
  initialValue = '',
  onSearchChange,
  debounceMs = 0,
  clearOnMount = false,
} = {}) => {
  const [searchTerm, setSearchTerm] = useState(initialValue);
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState(initialValue);

  // Clear search on mount if requested
  useEffect(() => {
    if (clearOnMount) {
      setSearchTerm('');
      setDebouncedSearchTerm('');
    }
  }, [clearOnMount]);

  // Debounce search term if debounceMs is specified
  useEffect(() => {
    if (debounceMs > 0) {
      const timer = setTimeout(() => {
        setDebouncedSearchTerm(searchTerm);
      }, debounceMs);

      return () => clearTimeout(timer);
    } else {
      setDebouncedSearchTerm(searchTerm);
    }
  }, [searchTerm, debounceMs]);

  // Call onSearchChange when debounced search term changes
  useEffect(() => {
    if (onSearchChange) {
      onSearchChange(debouncedSearchTerm);
    }
  }, [debouncedSearchTerm, onSearchChange]);

  const handleSearchChange = useCallback((event) => {
    const newSearchTerm = event.target.value;
    setSearchTerm(newSearchTerm);
  }, []);

  const handleClearSearch = useCallback(() => {
    setSearchTerm('');
  }, []);

  const setSearch = useCallback((value) => {
    setSearchTerm(value);
  }, []);

  return {
    searchTerm,
    debouncedSearchTerm,
    handleSearchChange,
    handleClearSearch,
    setSearch,
    isSearching: searchTerm.length > 0,
  };
};



// WEBPACK FOOTER //
// ./src/components/SearchBar/hooks/useSearchBar.js